import {Component, computed, inject, input, output} from '@angular/core';
import {Rating, WineProduct} from '@core/interfaces/generated/graphql';
import {PriceTableComponent} from '../price-table/price-table.component';
import {NgClass, NgOptimizedImage} from '@angular/common';
import {ExistsPipe} from '@core/pipes/exists.pipe';
import {SafeHtmlPipe} from '@core/pipes/safe-html.pipe';
import {AlgoliaInsightsService} from '@core/services/algolia-insights.service';
import {ProductTagsComponent} from '../product-tags/product-tags.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {RouterLink} from '@angular/router';
import {RouterContextDirective} from '@core/directives/router-context.directive';
import {ProductTopCtaComponent} from '@layout/components/product/product-top-cta/product-top-cta.component';
import {CustomerReviewsAndWishlistService} from '@core/services/customer-reviews-and-wishlist.service';
import {
  ProductCompactRatingComponent
} from '@layout/components/product/product-compact-rating/product-compact-rating.component';
import {
  ProductDiscountLabelComponent
} from '@layout/components/product/product-discount-label/product-discount-label.component';
import {
  ProductCustomBadgeComponent
} from '@layout/components/product/product-custom-badge/product-custom-badge.component';
import {CloudflareCacheTagService} from '@core/services/cloudflare-cache-tag.service';
import {ProductPriceService} from '@core/services/product-price.service';

@Component({
  selector: 'article[app-product-widget]',
  templateUrl: './product-widget.component.html',
  styleUrls: ['./product-widget.component.scss'],
  imports: [NgClass, ExistsPipe, SafeHtmlPipe, ProductDiscountLabelComponent, ProductCustomBadgeComponent, ProductCompactRatingComponent, PriceTableComponent, NgOptimizedImage, ProductTagsComponent, RouterLink, RouterContextDirective, ProductTopCtaComponent, ProductCompactRatingComponent]
})
export class ProductWidgetComponent {
  private algoliaInsightsService = inject(AlgoliaInsightsService);
  private modal = inject(NgbModal);
  private customerReviewsAndWishlistService = inject(CustomerReviewsAndWishlistService);
  private cloudflareCache = inject(CloudflareCacheTagService);
  private productPriceService = inject(ProductPriceService);


  readonly sendEvent = input<(event: string, payload: any, name: string) => void>();
  readonly productClickEvent = output<WineProduct>();

  product = input.required<WineProduct, WineProduct>({
    transform: (product: WineProduct) => {
      // Add Cloudflare cache tag when product is set
      if (product?.id) {
        this.cloudflareCache.addTag(`product-${product.id}`);
      }
      return product;
    }
  });
  index = input.required<number>();
  eventSourceName = input.required<string>();
  hideProductDefaultCta = input<boolean>(false);
  showCustomerRating = input<boolean>(true);
  queryID = input<string | undefined>(undefined);

  compressedImage = computed(() => this.getProductImageUrl(this.product()))
  readonly maxRatings = input<number>(3);
  customerRating = computed(() => this.getCustomerReviewAsRating(this.product()));
  productUrl = computed(() => '/' + this.product()?.nodePath);
  hitPosition = computed(() => this.index() + 1);
  additionalLinkContext = computed(() => {
    return {hitPosition: this.hitPosition(), eventSourceName: this.eventSourceName()}
  });
  ratings = computed(() => this.product()?.meta?.ratings
    ?.filter((rating) => {
      return !rating?.isPreviousVintage && ((rating?.rating ?? 0) > 0 || (rating?.starCount ?? 0) > 0 || (rating?.label?.length ?? 0) > 0)
    })
    .slice(0, this.maxRatings()) as Rating[]);

  addToCart(product: WineProduct) {
    const sendEvent = this.sendEvent();
    if (sendEvent) {
      sendEvent('conversion', product, 'Added to cart');
      return;
    }
    this.algoliaInsightsService.clickedAddToCart(product.id);
  }

  private getProductImageUrl(product: WineProduct): string | undefined {
    return product?.image?.small as string | undefined;
  }

  productClick(product: WineProduct) {
    this.productClickEvent.emit(product);
    const sendEvent = this.sendEvent();
    if (sendEvent) {
      sendEvent('click', product, 'Product Clicked');
      return;
    }
    this.algoliaInsightsService.clickedOnProduct(product.id);
  }

  openEnergyLabelDoc(product: WineProduct) {
    import('../../modals/image-modal/image-modal.component').then(comp => {
      const ref = this.modal.open(comp.ImageModalComponent);
      ref.componentInstance.title = $localize`Energimærke`;
      ref.componentInstance.imgSrc = product.meta?.energyLabelDoc;
    })
  }

  getCustomerReviewAsRating(product: WineProduct): Rating {
    let customerReview = this.customerReviewsAndWishlistService.customerReviews().find(review => review?.product?.id === product.id);
    return {
      rating: customerReview?.rating ?? 0,
      starCount: 5,
      name: $localize`Din rating`,
    };
  }

  canShowCustomerRating() {
    return this.showCustomerRating() && (this.customerRating().rating || 0) > 0;
  }

  // Check if product has custom badge HTML with a valid discount
  hasCustomBadge = computed(() => {
    const product = this.product();
    if (!product?.customBadgeHTML) {
      return false;
    }
    
    // Use the ProductPriceService to calculate discount percentage
    const discountPercentage = this.productPriceService.calculateDiscountPercentage(product);
    return discountPercentage > 0;
  });
}
